import { useEffect, useState, type FormEvent } from 'react'
import { AveryChat, AccountEnvContext } from '@parkify/account'
import type { AccountClient } from '@parkify/account'
import {
  fetchMe, patchProfile, addPlate, removePlate, fetchPaymentMethod, createBillingSession,
  fetchSubscriptions, fetchPermits, setAutoRenew, extendSession, extendQuote, fetchReceipt,
  emailReceipt, getEmail, startPhoneChange, verifyPhoneChange, sendCustomerChat, fileRefundRequest,
} from '../api'

/**
 * AveryAsk — the header "ask box" entry point to Avery.
 *
 * Lives in the portal header as a single-line text field. While it's empty
 * and unfocused it cycles a list of common requests through the placeholder
 * with a soft fade, so customers discover what Avery can do ("I need a
 * refund", "Send me my receipts", …). Submitting (Enter or the send button)
 * opens Avery in a modal and seeds the typed question as the first message.
 *
 * Avery is the first-party authenticated assistant (AveryChat → POST
 * /api/customer/chat) — it already knows the signed-in customer, so it can
 * answer about their own receipts, spend, and charges directly.
 *
 * AveryChat now lives in the shared @parkify/account package and reads its API
 * surface via useAccountEnv() rather than importing api.ts functions directly.
 * AveryAsk sits in the header, a SIBLING of <Home> (see App.tsx) — outside the
 * AccountEnvContext.Provider that AccountExperience/Home sets up internally —
 * so it supplies its own provider here, built from the same portal api.ts
 * functions Home.tsx wires into its AccountClient. Only the fields AveryChat
 * actually reads (client.sendCustomerChat/fetchReceipt/extendSession/
 * emailReceipt/fileRefundRequest) are exercised; the rest of AccountClient is
 * supplied for type completeness and to keep this one client shape reusable if
 * Avery's header entry point ever needs more of it.
 */
const askClient: AccountClient = {
  fetchMe, patchProfile, addPlate, removePlate, fetchPaymentMethod, createBillingSession,
  fetchSubscriptions, fetchPermits, setAutoRenew, extendSession, extendQuote, fetchReceipt,
  emailReceipt, getEmail, startPhoneChange, verifyPhoneChange, sendCustomerChat, fileRefundRequest,
}

// Rotating placeholder prompts. Phrased as the customer would type them — they
// double as discoverability ("oh, I can just ask for that") and, on submit,
// become the real first message handed to Avery.
const SUGGESTIONS = [
  'When does my session end?',
  'Send me my receipts',
  'How much have I paid in parking this month?',
  'Help me extend my parking session',
  'Where did I park?',
  'Why was I charged?',
]

const FADE_MS = 420 // opacity/slide transition for each placeholder swap
const HOLD_MS = 2800 // how long each prompt stays fully visible

/**
 * AveryModal — the centred Avery panel, shared by both header entry points
 * (the AveryAsk prompt box and the person-icon Support button). One
 * implementation so the two can't drift on chrome, scroll-lock, Escape, or the
 * `.pka` / AccountEnvContext wrapping AveryChat needs.
 *
 * `seed` is the question to auto-send on open; omit it and AveryChat opens on
 * its greeting + quick-start pills instead.
 */
export function AveryModal({ seed = '', onClose }: { seed?: string; onClose: () => void }) {
  // Lock body scroll and close on Escape for as long as this is mounted.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prevOverflow
      window.removeEventListener('keydown', onKey)
    }
  }, [onClose])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Ask Avery"
      onMouseDown={e => {
        if (e.target === e.currentTarget) onClose()
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(20, 10, 28, 0.55)',
        backdropFilter: 'blur(2px)',
        WebkitBackdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onMouseDown={e => e.stopPropagation()}
        style={{
          width: 'min(420px, 100%)',
          height: 'min(640px, calc(100dvh - 80px))',
          background: 'var(--color-white)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          boxShadow: '0 24px 60px rgba(0,0,0,0.4)',
        }}
      >
        {/* .pka wrapper: the shared AveryChat uses pka-btn-* classes + the
            :where(.pka) button reset, which only apply inside a .pka
            ancestor. This panel renders outside the account experience's own
            .pka wrapper, so without this, every button loses its brand fill.
            The AccountEnvContext.Provider is likewise local — see the
            askClient comment above. */}
        <div className="pka" style={{ height: '100%' }}>
          <AccountEnvContext.Provider value={{
            client: askClient,
            billingPortalEnabled: false,
            onFindParking: () => { window.location.hash = '/find' },
            onSignInAgain: () => { window.location.hash = '/' },
          }}>
            <AveryChat seed={seed} onClose={onClose} />
          </AccountEnvContext.Provider>
        </div>
      </div>
    </div>
  )
}

/**
 * AverySupportButton — the signed-in Support entry point in the portal header.
 *
 * Signed in, "Support" means Avery: she already knows who the customer is and
 * can pull their receipts, explain a charge, extend a session, or file a refund
 * request in-thread. So this is a person icon that opens the chat straight
 * away, rather than @parkify/ui's SupportButton popover offering only Text/Call
 * — the SMS and voice lines are the same Avery, just slower and without the
 * customer's session attached. Signed OUT the header keeps SupportButton (see
 * App.tsx): /api/customer/chat is authenticated, so there'd be nothing to open.
 *
 * The person mark matches the consumer checkout's header account icon
 * (pk-brand__acct in parkify-pay/consumer/src/ui.tsx) so support looks like the
 * same affordance across surfaces.
 */
export function AverySupportButton() {
  const [open, setOpen] = useState(false)
  const close = () => setOpen(false)
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Support — chat with Avery"
        title="Support"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 38,
          height: 38,
          borderRadius: '50%',
          border: '1px solid var(--color-brand-border)',
          background: 'var(--color-brand-light)',
          color: 'var(--color-brand)',
          cursor: 'pointer',
          transition: 'background 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.background = 'var(--color-white)'
          e.currentTarget.style.borderColor = 'var(--color-brand)'
          e.currentTarget.style.boxShadow = '0 2px 10px rgba(127,63,152,0.25)'
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background = 'var(--color-brand-light)'
          e.currentTarget.style.borderColor = 'var(--color-brand-border)'
          e.currentTarget.style.boxShadow = 'none'
        }}
      >
        <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 4-6.5 8-6.5s8 2.5 8 6.5" />
        </svg>
      </button>
      {open && <AveryModal onClose={close} />}
    </>
  )
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
  )
}

export function AveryAsk() {
  const [value, setValue] = useState('')
  const [focused, setFocused] = useState(false)
  const [idx, setIdx] = useState(0)
  const [phVisible, setPhVisible] = useState(true)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const reduceMotion = prefersReducedMotion()
  // The rotating prompt only shows while the field is empty + unfocused.
  const showPlaceholder = value.length === 0 && !focused

  // Cycle the placeholder. Pauses whenever the field is in use (typed-in or
  // focused) and is disabled entirely under prefers-reduced-motion.
  useEffect(() => {
    if (!showPlaceholder || reduceMotion) {
      setPhVisible(true)
      return
    }
    let fadeTimer: number | undefined
    const interval = window.setInterval(() => {
      setPhVisible(false) // fade the current prompt out…
      fadeTimer = window.setTimeout(() => {
        setIdx(i => (i + 1) % SUGGESTIONS.length) // …swap while invisible…
        setPhVisible(true) // …then fade the next one in.
      }, FADE_MS)
    }, HOLD_MS)
    return () => {
      window.clearInterval(interval)
      if (fadeTimer) window.clearTimeout(fadeTimer)
    }
  }, [showPlaceholder, reduceMotion])

  const ask = (raw: string) => {
    setQuery(raw.trim())
    setOpen(true)
  }

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    ask(value)
    // Clear the box so it returns to the rotating-prompt state once the chat
    // closes — the question now lives in the Avery conversation.
    setValue('')
  }

  const close = () => setOpen(false)

  return (
    <>
      <form
        onSubmit={onSubmit}
        role="search"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          width: '100%',
          background: focused ? 'var(--color-white)' : 'var(--color-gray-100)',
          border: `1px solid ${focused ? 'var(--color-brand)' : 'var(--color-gray-300)'}`,
          borderRadius: 'var(--radius-full)',
          padding: '7px 7px 7px 14px',
          transition: 'border-color 140ms ease, background 140ms ease, box-shadow 140ms ease',
          boxShadow: focused ? '0 0 0 3px var(--color-brand-light)' : 'none',
        }}
      >
        {/* Avery mark — brand-purple disc, white "A" (the WidgetMark avatar
            treatment) — flipped from white-disc/purple-"A" now that the box
            sits on a light header instead of the purple gradient. */}
        <span
          aria-hidden
          style={{
            flex: '0 0 auto',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 24,
            height: 24,
            borderRadius: '50%',
            background: 'var(--color-brand)',
            color: 'var(--color-white)',
            fontWeight: 800,
            fontSize: 12,
            letterSpacing: -0.3,
          }}
        >
          A
        </span>

        {/* Input + the rotating placeholder overlaid on top of it. We can't
            cross-fade a native ::placeholder, so the prompt is a separate
            absolutely-positioned span that fades while the real input stays
            transparent underneath. */}
        <div style={{ position: 'relative', flex: 1, minWidth: 0, height: 24 }}>
          <input
            value={value}
            onChange={e => setValue(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            aria-label="Ask Avery"
            autoComplete="off"
            enterKeyHint="send"
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: 'var(--color-black)',
              fontSize: 14,
              fontWeight: 500,
              padding: 0,
              margin: 0,
            }}
          />
          {showPlaceholder && (
            <span
              aria-hidden
              style={{
                position: 'absolute',
                insetInlineStart: 0,
                top: 0,
                height: '100%',
                maxWidth: '100%',
                display: 'flex',
                alignItems: 'center',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                pointerEvents: 'none',
                color: 'var(--color-gray-600)',
                fontSize: 14,
                fontWeight: 500,
                opacity: phVisible ? 1 : 0,
                transform: phVisible ? 'translateY(0)' : 'translateY(3px)',
                transition: `opacity ${FADE_MS}ms ease, transform ${FADE_MS}ms ease`,
              }}
            >
              {SUGGESTIONS[idx]}
            </span>
          )}
        </div>

        {/* Send */}
        <button
          type="submit"
          aria-label="Ask Avery"
          style={{
            flex: '0 0 auto',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: 'var(--color-brand)',
            color: 'var(--color-white)',
            transition: 'transform 120ms ease, box-shadow 120ms ease',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.boxShadow = '0 2px 10px rgba(127,63,152,0.35)'
          }}
          onMouseLeave={e => {
            e.currentTarget.style.boxShadow = 'none'
          }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M5 12h14M13 6l6 6-6 6"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </form>

      {open && <AveryModal seed={query} onClose={close} />}
    </>
  )
}
